/**
 * /legal/terms — Terms of Service.
 *
 * MVP draft — SME / legal counsel review before launch.
 * Bumping `TOS_VERSION` (in @bimassess/db) on any material change re-prompts
 * existing users via the stale-consent modal (J-X13 § Steps existing user).
 *
 * Substrate: LegalPage wrapper from ./LegalPage.tsx.
 */

import type { ReactElement } from 'react';
import { TOS_VERSION } from '@bimassess/db/legal-versions';
import { LegalPage } from './LegalPage.tsx';

export function TermsOfServicePage(): ReactElement {
  return (
    <LegalPage
      eyebrow="Legal"
      title="Terms of Service"
      version={TOS_VERSION}
      draft
    >
      <p>
        These Terms of Service ("Terms") govern your use of BIMAssess. By
        creating an account or taking an assessment you agree to these Terms.
      </p>

      <h2 className="mt-8 font-sans text-h3 font-medium tracking-tight">
        1. Acceptable use
      </h2>
      <p>
        You agree to use BIMAssess only for the purpose of taking and
        administering assessments honestly. You will not attempt to bypass
        security controls, share questions or answers, or use automated tools
        to submit responses.
      </p>

      <h2 className="mt-8 font-sans text-h3 font-medium tracking-tight">
        2. Account responsibilities
      </h2>
      <p>
        You are responsible for keeping your sign-in credentials confidential
        and for any activity that occurs under your account. Notify us
        immediately at{' '}
        <a href="mailto:support@bimassess.com" className="underline">
          support@bimassess.com
        </a>{' '}
        if you suspect unauthorised access.
      </p>

      <h2 className="mt-8 font-sans text-h3 font-medium tracking-tight">
        3. Integrity and credentialing
      </h2>
      <p>
        Each assessment requires a candidate-side affirmation (the integrity
        pledge) before the timer starts. Violations of the pledge — including
        unauthorised assistance or question-sharing — may result in your
        result being voided and your account being suspended.
      </p>

      <h2 className="mt-8 font-sans text-h3 font-medium tracking-tight">
        4. Content and intellectual property
      </h2>
      <p>
        Assessment content (questions, task artefacts, marking rubrics) is
        owned by BIMAssess or its content partners. You may not copy,
        reproduce, or republish assessment content without prior written
        permission.
      </p>

      <h2 className="mt-8 font-sans text-h3 font-medium tracking-tight">
        5. Privacy
      </h2>
      <p>
        Our handling of personal information is governed by the{' '}
        <a href="/legal/privacy" className="underline">
          Privacy Policy
        </a>
        .
      </p>

      <h2 className="mt-8 font-sans text-h3 font-medium tracking-tight">
        6. Disclaimers and liability
      </h2>
      <p>
        BIMAssess is provided on an "as is" basis. To the maximum extent
        permitted by Australian Consumer Law, we exclude implied warranties
        and limit our liability for indirect or consequential loss. Nothing in
        these Terms excludes rights that cannot be excluded under applicable
        law.
      </p>

      <h2 className="mt-8 font-sans text-h3 font-medium tracking-tight">
        7. Changes
      </h2>
      <p>
        We may update these Terms. Material changes prompt all existing users
        to review and re-accept on next sign-in. The current version string
        is shown at the top of this page.
      </p>

      <h2 className="mt-8 font-sans text-h3 font-medium tracking-tight">
        8. Governing law
      </h2>
      <p>
        These Terms are governed by the laws of Australia.
      </p>
    </LegalPage>
  );
}
